import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import WizardStep from './WizardStep';
import PreviewPanel from './PreviewPanel';
import { useDideWriter, UseDideWriterOptions } from './useDideWriter';
import { benefitKeyToColumn, DIDE_WRITER_QUESTIONS, TONE_QUESTION } from './questions';
import { EXTRA_INFO_MAX_LENGTH } from './types';
import type { BenefitKey, GenerateErrorCode, Tone } from './types';
import type { AppliedField, PreviewApplyPayload } from './PreviewPanel';

export interface DideWriterApplyPayload {
  draft_id: number;
  description?: string;
  // Sanitized HTML for hosts that store HTML. Prefer it over `description`.
  description_html?: string;
  title?: string;
  experience_required?: string;
  // Maps directly onto job form state: `{ benefit_medical: true, ... }`.
  benefitColumns: Partial<Record<string, boolean>>;
  applied_fields: AppliedField[];
}

export interface DideWriterModalProps extends UseDideWriterOptions {
  show: boolean;
  onHide: () => void;
  onApply: (payload: DideWriterApplyPayload) => void;
  availableFields?: AppliedField[];
}

// Maps error codes to human copy. Kept here (not in the hook) so the wording is
// tied to the UI presentation.
const ERROR_MESSAGES: Record<GenerateErrorCode, string> = {
  invalid_input: 'Some details are missing or too long. Please check the form and your notes.',
  ai_unavailable: 'We could not write a description right now. Please try again in a minute.',
  internal_error: 'Something went wrong on our end. Please try again.',
  feature_disabled: 'The description writer is not available for this practice.',
  not_practice_user: 'You need to be signed in as a practice user to use the description writer.',
  forbidden_practice: 'You do not have access to that practice.',
  forbidden_draft: 'That draft is no longer available.',
  forbidden_job: 'You do not have access to that job.',
  not_found: 'We couldn\'t find that draft anymore.',
  rate_limited: 'You\'ve reached the limit for the description writer for now. Try again later.',
  unauthenticated: 'Your session has expired. Please sign in again to keep using the description writer.',
  unknown: 'Something went wrong. Please try again.',
};

export const DideWriterModal: React.FC<DideWriterModalProps> = ({
  show,
  onHide,
  onApply,
  availableFields,
  ...options
}) => {
  const dw = useDideWriter(options);

  // Reset wizard state whenever the modal is closed by any path (parent
  // toggling `show`, route change, etc.) so the next open starts fresh.
  useEffect(() => {
    if (!show) dw.reset();
    // `reset` is stable via useCallback, intentionally omit dw deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const close = () => {
    dw.reset();
    onHide();
  };

  const handleApplyFromPreview = (applied: PreviewApplyPayload) => {
    const benefitColumns: Record<string, boolean> = {};
    for (const key of applied.benefits as BenefitKey[]) {
      benefitColumns[benefitKeyToColumn(key)] = true;
    }
    onApply({
      draft_id: applied.draft_id,
      description: applied.description,
      description_html: applied.description_html,
      title: applied.title,
      experience_required: applied.experience_required,
      benefitColumns,
      applied_fields: applied.applied_fields,
    });
    close();
  };

  const renderHeaderProgress = () => (
    <div className="small text-muted mb-2" aria-live="polite">
      Optional question {dw.stepIndex + 1} of {dw.totalSteps}
    </div>
  );

  // First screen: one free-form box for anything the employer wants in the
  // post, plus tone. The job form fields are sent automatically.
  const renderIntro = () => (
    <>
      <Modal.Body>
        <label htmlFor="dide-extra-info" className="form-label fw-semibold mb-1">
          Anything else candidates should know?
        </label>
        <div id="dide-extra-info-helper" className="form-text mt-0 mb-2">
          We already use this form and your practice profile. Add the team, perks, or what makes
          this role special.
        </div>
        <textarea
          id="dide-extra-info"
          className="form-control"
          rows={4}
          maxLength={EXTRA_INFO_MAX_LENGTH}
          value={dw.extraInfo}
          aria-describedby="dide-extra-info-helper"
          placeholder="e.g., Small, friendly team. We close at noon on Fridays and pay for CE courses."
          onChange={(e) => dw.setExtraInfo(e.target.value)}
        />
        <div className="form-text text-end mb-2">
          {dw.extraInfo.length} / {EXTRA_INFO_MAX_LENGTH}
        </div>
        <WizardStep
          question={TONE_QUESTION}
          value={dw.answers.q4}
          onChange={(v) => dw.setAnswer('q4', v as Tone)}
        />
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <button type="button" className="btn btn-link px-0" onClick={close}>
          Cancel
        </button>
        <div className="d-flex flex-wrap justify-content-end gap-2">
          <button type="button" className="btn btn-outline-primary" onClick={dw.openQuestions}>
            Answer a few optional questions
          </button>
          <button type="button" className="btn btn-primary" onClick={() => dw.submit()}>
            Write it for me
          </button>
        </div>
      </Modal.Footer>
    </>
  );

  const renderWizard = () => {
    if (!dw.showQuestions) return renderIntro();
    if (!dw.currentQuestion) return null;
    const isLastStep = dw.stepIndex === dw.totalSteps - 1;
    return (
      <>
        <Modal.Body>
          {renderHeaderProgress()}
          <WizardStep
            question={dw.currentQuestion}
            value={dw.answers[dw.currentQuestion.id]}
            onChange={(v) => dw.setAnswer(dw.currentQuestion.id, v as never)}
          />
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <button type="button" className="btn btn-link px-0" onClick={dw.back}>
            Back
          </button>
          {isLastStep ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => dw.submit()}
              disabled={!dw.canSubmit}
            >
              Write it for me
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={dw.next}
              disabled={!dw.canContinue}
            >
              Next
            </button>
          )}
        </Modal.Footer>
      </>
    );
  };

  const renderLoading = () => (
    <Modal.Body>
      <div className="text-center py-5" role="status" aria-live="polite">
        <div className="spinner-border text-primary mb-3" />
        <p className="mb-0">Writing your description.</p>
        <small className="text-muted">This can take up to a minute.</small>
      </div>
    </Modal.Body>
  );

  const renderError = () => {
    const code = dw.error?.code ?? 'unknown';
    const message = ERROR_MESSAGES[code] || dw.error?.error || ERROR_MESSAGES.unknown;
    return (
      <>
        <Modal.Body>
          <div className="alert alert-danger mb-0" role="alert">
            <strong>We couldn't generate that draft.</strong>
            <div>{message}</div>
            {code === 'rate_limited' && dw.error?.limit && dw.error?.scope !== 'global_daily' && (
              <div className="small mt-1">
                Limit: {dw.error.limit} generations per {dw.error.scope === 'daily' ? 'day' : 'hour'}.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={close}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => dw.setPhase('wizard')}
          >
            Back
          </button>
          {code !== 'feature_disabled' &&
            code !== 'forbidden_practice' &&
            code !== 'forbidden_job' &&
            code !== 'unauthenticated' && (
              <button type="button" className="btn btn-primary" onClick={() => dw.submit()}>
                Try again
              </button>
            )}
        </Modal.Footer>
      </>
    );
  };

  return (
    // `scrollable` keeps the header and footer pinned and scrolls only the
    // body, so the action buttons stay reachable on short laptop screens.
    <Modal
      show={show}
      onHide={close}
      size="lg"
      centered
      scrollable
      aria-labelledby="dide-writer-title"
      data-testid="dide-writer-modal"
    >
      <Modal.Header closeButton className="py-2">
        <Modal.Title as="h5" id="dide-writer-title">Write your job description</Modal.Title>
      </Modal.Header>
      {dw.phase === 'wizard' && renderWizard()}
      {dw.phase === 'loading' && !dw.draft && renderLoading()}
      {dw.phase === 'error' && renderError()}
      {(dw.phase === 'preview' || (dw.phase === 'loading' && dw.draft)) && dw.draft && (
        <PreviewPanel
          draft={dw.draft}
          onApply={handleApplyFromPreview}
          onRegenerate={(hint) => dw.regenerate(hint)}
          onCancel={close}
          regenerating={dw.phase === 'loading'}
          regenerateError={dw.phase === 'preview' ? dw.error : null}
          onDismissRegenerateError={dw.dismissError}
          endpointBase={options.endpointBase}
          availableFields={availableFields}
        />
      )}
    </Modal>
  );
};

export { DIDE_WRITER_QUESTIONS };
export default DideWriterModal;
